import axios from 'axios';
import { ApiResponse, AuthResponse, LoginCredentials } from '../types';
import { storage } from './helpers';

// Hardcoded API URL for deployed backend on Render
const API_BASE_URL = 'https://backend-repo-infura.onrender.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = storage.get<string>('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      storage.remove('token');
      storage.remove('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', credentials);
    return response.data.data!;
  },
  
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
    storage.remove('token');
    storage.remove('user');
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data.data;
  },
};

// Blog API
export const blogAPI = {
  getBlogs: async (params?: { page?: number; limit?: number; search?: string }) => {
    const response = await api.get('/blogs', { params });
    return response.data;
  },
  
  getBlog: async (slug: string) => {
    const response = await api.get(`/blogs/${slug}`);
    return response.data.data;
  },
  
  createBlog: async (blogData: FormData) => {
    const response = await api.post('/blogs', blogData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },
  
  updateBlog: async (id: string, blogData: FormData) => {
    const response = await api.put(`/blogs/${id}`, blogData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },
  
  deleteBlog: async (id: string) => {
    await api.delete(`/blogs/${id}`);
  },
};

// Case Study API
export const caseStudyAPI = {
  getCaseStudies: async () => {
    const response = await api.get('/case-studies');
    return response.data.data;
  },
  
  getCaseStudy: async (id: string) => {
    const response = await api.get(`/case-studies/${id}`);
    return response.data.data;
  },
  
  createCaseStudy: async (caseStudyData: FormData) => {
    const response = await api.post('/case-studies', caseStudyData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },
  
  updateCaseStudy: async (id: string, caseStudyData: FormData) => {
    const response = await api.put(`/case-studies/${id}`, caseStudyData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },
  
  deleteCaseStudy: async (id: string) => {
    await api.delete(`/case-studies/${id}`);
  },
};

// Message API
export const messageAPI = {
  getMessages: async () => {
    const response = await api.get('/messages');
    return response.data.data;
  },
  
  createMessage: async (messageData: { name: string; email: string; message: string }) => {
    const response = await api.post('/messages', messageData);
    return response.data.data;
  },
  
  markAsRead: async (id: string) => {
    const response = await api.patch(`/messages/${id}/read`);
    return response.data.data;
  },
  
  deleteMessage: async (id: string) => {
    await api.delete(`/messages/${id}`);
  },
};

// User API
export const userAPI = {
  getUsers: async () => {
    const response = await api.get('/users');
    return response.data.data;
  },
  
  createUser: async (userData: { name: string; email: string; password: string; role: string }) => {
    const response = await api.post('/users', userData);
    return response.data.data;
  },
  
  updateUser: async (id: string, userData: Partial<{ name: string; email: string; role: string }>) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data.data;
  },
  
  deleteUser: async (id: string) => {
    await api.delete(`/users/${id}`);
  },
};

// Contact API
export const contactAPI = {
  submitContactForm: async (formData: { name: string; email: string; subject: string; message: string }) => {
    const response = await api.post('/contact', formData);
    return response.data.data;
  },
};

export default api;